import { useEffect, useState } from "react";
import api from "@/lib/api";
import { generatePDF, generateJPGZip, PAGE_SIZES } from "@/lib/idcRender";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { FileDown, Image, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { Loading } from "@/components/Loading";

export default function GenerateTab() {
  const [templates, setTemplates] = useState([]);
  const [datasets, setDatasets] = useState([]);
  const [templateId, setTemplateId] = useState("");
  const [datasetId, setDatasetId] = useState("");
  const [dataset, setDataset] = useState(null);
  const [selectedRows, setSelectedRows] = useState({});
  const [filename, setFilename] = useState("");
  const [pageSize, setPageSize] = useState("a4");
  const [gapMm, setGapMm] = useState("2");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/idc/templates").then((r) => setTemplates(r.data)).catch(() => {}),
      api.get("/idc/datasets").then((r) => setDatasets(r.data)).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!datasetId) { setDataset(null); setSelectedRows({}); return; }
    api.get(`/idc/datasets/${datasetId}`).then((r) => {
      setDataset(r.data);
      const all = {};
      (r.data.rows || []).forEach((_, i) => (all[i] = true));
      setSelectedRows(all);
    }).catch(() => {});
  }, [datasetId]);

  const template = templates.find((t) => t.id === templateId);
  const rows = dataset?.rows || [];
  const fields = dataset?.fields || [];
  const labelField = fields.find((f) => f.type !== "photo");
  const chosen = rows.filter((_, i) => selectedRows[i]);

  const toggleAll = (v) => {
    const s = {};
    rows.forEach((_, i) => (s[i] = v));
    setSelectedRows(s);
  };

  const run = async (mode) => {
    if (!template) return toast.error("Select a template first");
    if (!template.layout || template.layout.length === 0) return toast.error("No fields designed on this template. Use Edit/Design in the Templates tab.");
    if (chosen.length === 0) return toast.error("Select at least one row");
    setBusy(true);
    const tid = toast.loading(`Generating ${chosen.length} ${mode.toUpperCase()}...`);
    try {
      const fname = filename.trim() || "id-cards";
      if (mode === "pdf") await generatePDF(template, template.layout, chosen, fields, fname, PAGE_SIZES.find((p) => p.value === pageSize) || PAGE_SIZES[0], gapMm.trim() === "" ? undefined : parseFloat(gapMm));
      else await generateJPGZip(template, template.layout, chosen, fields, fname);
      toast.success("Download ready!", { id: tid });
    } catch (e) {
      toast.error("Generation failed: " + (e.message || ""), { id: tid });
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="generate-tab">
      {loading && <Loading />}
      <div className="bg-card border border-border rounded-xl p-5 grid sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Template</Label>
          <Select value={templateId} onValueChange={setTemplateId}>
            <SelectTrigger data-testid="generate-template-select"><SelectValue placeholder="Select template" /></SelectTrigger>
            <SelectContent>{templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name} ({(t.layout || []).length} fields)</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Data File</Label>
          <Select value={datasetId} onValueChange={setDatasetId}>
            <SelectTrigger data-testid="generate-dataset-select"><SelectValue placeholder="Select data" /></SelectTrigger>
            <SelectContent>{datasets.map((d) => <SelectItem key={d.id} value={d.id}>{d.name} ({(d.rows || []).length} rows)</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>File Name</Label>
          <Input value={filename} onChange={(e) => setFilename(e.target.value)} placeholder="e.g. school-ids-2026" data-testid="generate-filename-input" />
        </div>
        <div className="space-y-2">
          <Label>PDF Page Size</Label>
          <Select value={pageSize} onValueChange={setPageSize}>
            <SelectTrigger data-testid="generate-pagesize-select"><SelectValue /></SelectTrigger>
            <SelectContent>{PAGE_SIZES.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Margin Between Templates (In MM)</Label>
          <Input type="number" step="any" min="0" value={gapMm} onChange={(e) => setGapMm(e.target.value)} placeholder="2" data-testid="generate-gap-input" />
        </div>
      </div>

      {dataset && (
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <div className="px-4 py-2.5 border-b border-border flex items-center justify-between">
            <label className="flex items-center gap-2 text-sm font-semibold cursor-pointer">
              <Checkbox checked={chosen.length === rows.length && rows.length > 0} onCheckedChange={toggleAll} data-testid="generate-select-all" />
              Select All ({chosen.length}/{rows.length})
            </label>
          </div>
          <div className="max-h-72 overflow-y-auto">
            {rows.map((row, i) => (
              <label key={i} className="flex items-center gap-3 px-4 py-2 border-t border-border hover:bg-slate-50 cursor-pointer text-sm" data-testid={`generate-row-${i}`}>
                <Checkbox checked={!!selectedRows[i]} onCheckedChange={(v) => setSelectedRows({ ...selectedRows, [i]: v })} />
                <span className="text-slate-400 text-xs w-6">{i + 1}</span>
                <span className="font-medium">{labelField ? row[labelField.key] || "—" : `Row ${i + 1}`}</span>
              </label>
            ))}
            {rows.length === 0 && <p className="px-4 py-6 text-center text-muted-foreground text-sm">No rows in this data file.</p>}
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-3">
        <Button onClick={() => run("pdf")} disabled={busy} data-testid="generate-pdf-btn" className="gap-1.5 bg-cyan-600 hover:bg-cyan-700 text-white">
          <FileDown className="h-4 w-4" /> Generate PDF ({(PAGE_SIZES.find((p) => p.value === pageSize) || PAGE_SIZES[0]).label.split(" (")[0]})
        </Button>
        <Button onClick={() => run("jpg")} disabled={busy} data-testid="generate-jpg-btn" variant="outline" className="gap-1.5">
          <Image className="h-4 w-4" /> Generate JPG (ZIP)
        </Button>
      </div>
      <p className="text-xs text-muted-foreground flex items-center gap-1.5">
        <Wand2 className="h-3.5 w-3.5" /> PDF places cards at their actual size on the selected page size; overflow automatically continues on the next page.
      </p>
    </div>
  );
}
